import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Divider, IconButton, InputBase, Paper, Typography } from '@mui/material';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import EditProjectModal from './EditProjectModal';
import { Link } from 'react-router-dom';
import { Container } from "reactstrap";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PaginationTableWithFuncBtn from '../../../components/Util/PaginationTableWithFuncBtn';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from "@mui/icons-material/Add";
import toastAlert from '../../../js/Base/ToastAlert';
import DeleteModal from '../../../components/Util/DeleteModal';
import moment from "moment";
import {
  getProjectsByCompanyId,
  getCompanyMembersByNoList,
  getCompanyMembersByCompanyId,
} from '../../../api/companyApi';
import { getAccountsByNoList } from '../../../api/accountApi';
import { editProject, getProjectMemberInfo } from '../../../api/projectApi';
import { useDispatch } from "react-redux";
import { fetchBusinessMembers } from '../../../store/slices/businessSlice';

export default function ProcessManagementPage() {
  const dispatch = useDispatch();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const companyUuid = localStorage.getItem("company_uuid");

  const getProjectsByWebApiTest = async () => {
    try {
      setLoading(true);
      const projectRes = await getProjectsByCompanyId(companyUuid);
      const projectData = projectRes.data;
      if (!projectData.success) {
        toastAlert.autoClose(true).showError("取得專案失敗");
        return;
      }

      const projectList = projectData.projects || [];
      const memberUuidList = projectList.map((p) => p.company_member_uuid);
      const memberRes = await getCompanyMembersByNoList(memberUuidList);
      const compMembers = memberRes.data.members || [];
      projectList.forEach((project) => {
        project.owner_member =
          compMembers.find((m) => project.company_member_uuid === m.uuid) || null;
      });

      const acctUuidList = projectList
        .map((p) => p.owner_member?.acct_uuid)
        .filter((no) => no != null);

      if (acctUuidList.length > 0) {
        const acctRes = await getAccountsByNoList(acctUuidList);
        const accounts = acctRes.data.accounts || [];
  
        projectList.forEach((project) => {
          if (project.owner_member) {
            const m = project.owner_member;
            project.owner_member.account =
              accounts.find((a) => a.uuid === m.acct_uuid) || null;
          }
        });
      }
      setProjects(projectList);
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("系統錯誤，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  const getCompMembersByWebApi = async () => {
    if (!companyUuid) {
      toastAlert.autoClose(true).showError("查無資料，請聯絡系統管理員");
      return;
    }

    try {
      const response = await getCompanyMembersByCompanyId(companyUuid);
      const data = response.data;

      if (data.success) {
        setCompanyMembers(data.members);
      } else {
        toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    }
  };


  useEffect(() => {
    getProjectsByWebApiTest();
    getCompMembersByWebApi();
  }, []);

  const handleEdit = (id) => {
    toggleModal(id);
  };

  const handleDelete = (id) => {
    handleDeleteModalOpen(id)
  };

  const toggleModal = (selectedProjectNo = null) => {
    const project = projects.find(p => p.uuid === selectedProjectNo) || null;
    setSelectedProject(project);
    setIsModalOpen(true);
  };
  const handleEditModalClose = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleDeleteModalOpen = (selectedProjectNo) => {
    const project = projects.find(p => p.uuid === selectedProjectNo);
    setSelectedProject(project || null);
    setDeleteModalOpen(true);
  };
  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setSelectedProject(null);
  };


  const handleDeleteConfirm = async () => {
    if (!selectedProject) return;
    try {
      const success = await editProject({
        project_uuid: selectedProject.uuid,
        acct_email: localStorage.getItem("user_email"),
        status: 0,
      });
      if (success) {
        toastAlert.autoClose(true).showSuccess("刪除成功");
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("刪除失敗，請稍後再試");
    } finally {
      handleDeleteModalClose();
      getProjectsByWebApiTest();
      
      const projectUuid = localStorage.getItem("project_uuid");
      if (selectedProject.uuid === projectUuid) {
        reassign_project();
      }
    }
  };

  const reassign_project = async () => {    
    try {
      const userName = localStorage.getItem("user_email");
      const result = await getProjectMemberInfo(userName);
      if (result.success) {
        const data = result.info
        localStorage.setItem('company_id', data.company_id);
        localStorage.setItem('company_uuid', data.company_uuid);
        localStorage.setItem("project_id", data.project_id);
        localStorage.setItem('project_uuid', data.project_uuid);
        localStorage.setItem('project_name', data.project_name);
      }
      dispatch(fetchBusinessMembers());
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    }
  }

  const col_name = [
    "名稱", "擁有者", "開始時間", "OID", "備註", "功能",
  ];

  const dataFrame = [
    { keyName: 'name' },
    { keyName: 'owner_member.account.name' },
    { keyName: 'begin_dt' },
    { keyName: 'oid' },
    { keyName: 'memo' },
    { keyName: 'func', isClick: true, editBtn: true, delBtn: true },
  ];

  const col_size = ['17%', '17%', '17%', '17%', '17%', '15%'];
  const newProjects = projects.map((project) => {
    return {
      ...project,
      begin_dt: !project.begin_dt
        ? ''
        : moment.utc(project.begin_dt).local().format('YYYY-MM-DD HH:mm'),
    };
  });

  return (
    <React.Fragment>
      <Container className="mt-2">
        <Breadcrumbs
          className="link-area"
          style={{ padding: "0 0 20px 0", fontWeight: 'bold'}}
          separator={<NavigateNextIcon style={{ fontSize: '20px', fontWeight: 'bold' }} />}
          aria-label="breadcrumb"
        >
          <Link to="/admin-management" style={{ color: '#1d1d1d', fontSize: '20px', textDecoration: 'none', fontWeight: 'bold' }}>
            <p style={{ marginBottom: '0px' }}>公司管理</p>
          </Link>
          <Link to="/company/process-management" style={{ color: '#1d1d1d', fontSize: '20px', textDecoration: 'none', fontWeight: 'bold' }}>
            <p style={{ marginBottom: '0px' }}>專案管理</p>
          </Link>
        </Breadcrumbs>

        <Card style={{ marginBottom: '50px'}}>
          <Card.Header style={{ backgroundColor: 'transparent' }}>
            <strong style={{ fontSize:'16px', fontWeight: 'bold', fontFamily: 'Arial, sans-serif' }}>專案管理</strong>
            <IconButton
              onClick={() => toggleModal(null)}
                sx={{
                  color: "black",
                  width: 32,
                  height: 32,
                }}
              >
                <Typography variant="h6" component="span" sx={{ lineHeight: 1 }}>
                  <AddIcon fontSize="medium" />
                </Typography>
              </IconButton>
          </Card.Header>

        <Card.Body>
          <Row>
            <Col md="4">
              <Paper variant="outlined" component="form" sx={{ p: '2px 2px', display: 'flex', alignItems: 'left', height: '32px' }}>
                <InputBase
                  sx={{ flex: 1, p: '10px' }}
                  style={{ fontSize: '12px', boxShadow: 'none' }}
                  placeholder="請輸入姓名或帳號"
                  inputProps={{ 'aria-label': 'search keyword' }}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
                <IconButton type="button" sx={{ p: '12px' }} aria-label="search">
                  <SearchIcon style={{ color: '#E2E2E2' }} />
                </IconButton>
              </Paper>
            </Col>
          </Row>

          {loading ? (
            <div className="d-flex justify-content-center my-3">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <div className="mt-3">
              <PaginationTableWithFuncBtn
                rowData={newProjects.filter(
                  (m) => m.name.indexOf(inputValue) > -1 || m.owner_member.account.name.indexOf(inputValue) > -1
                )}
                colName={col_name}
                dataFrame={dataFrame}
                col_size={col_size}
                delFunc={handleDelete}
                editModalOpen={handleEdit}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      <DeleteModal
        isModalOpen={deleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleUpdate={handleDeleteConfirm}
        title={'刪除專案'}
        content={`請確認是否刪除此專案：${selectedProject?.name || ''}`}
      />
      {isModalOpen && (
        <EditProjectModal
          isCreate={selectedProject === null}
          project={selectedProject}
          companyMembers={companyMembers}
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          handleClose={handleEditModalClose}
          submitEffect={getProjectsByWebApiTest}
        />
      )} 
      </Container>
    </React.Fragment>
  );
}
